import { useInView } from '@lib/hooks/useInView';
import { useMediaThumbnail } from '@lib/image/thumbnail';
import { LibraryStore } from '@stores/Library/Library';
import { RefObject, useCallback, useEffect, useState } from 'react';

/** Attempts to walk down `latestMediaIds` before giving up on a thumbnail. */
const MAX_FALLBACK_ATTEMPTS = 5;

/**
 * How far beyond its scroll window a library cover starts loading, as a share of that
 * scroller's own height above and below.
 *
 * A percentage rather than pixels because every surface is a different size: the picker
 * popover's scroll box is `max-height: 380px`, its mobile drawer is `60vh`, and the Libraries
 * surfaces scroll at a page-level wrapper. Percentages resolve against the root's own height,
 * so no row-height maths and nothing to keep in sync with a stylesheet.
 *
 * Split by layout because a percentage of *height* cannot see column count, and that is what
 * actually decides how many requests a window admits. `repeat(auto-fill, 210px)` gives the grid
 * 6–8 columns on a desktop screen, so the same margin there buys 6–8× the covers it buys in a
 * single-column list. Measured at scroll-top, where only the visible screenful plus the margin
 * below it applies:
 *
 * - picker rows, ~96px in a 380px popover → ~10 rows
 * - mobile list, 73px rows → ~22 rows
 * - grid, 299px row pitch: ~5–6 cards on a one-column phone, ~40–53 on a 6–8 column desktop
 *
 * The desktop grid is still the loose one; the margin is not the dominant term there. Revisit
 * against `libraryVerbPickerOpened.libraryCount` if real accounts say so.
 */
const LIBRARY_COVER_OVERSCAN = {
  /** Multi-column. Deliberately tighter — see above. */
  grid: '150% 0px',
  /** Single column, where a screenful is a handful of items. */
  list: '200% 0px',
} as const;

interface UseLibraryThumbnailOptions {
  /** The library's persisted thumbnail choice (`userMetadata.thumbnailMediaId`). */
  thumbnailMediaId?: string;
  /** Transient hover preview, which wins over everything while set. */
  hoveredMediaId?: string | null;
  /**
   * Skip resolution entirely. The underlying hook is still *called*
   * unconditionally (see the note below); only its arguments are withheld, so
   * a disabled thumbnail costs neither a preview token nor an image fetch.
   *
   * Deliberately a reason-agnostic flag rather than an enum — callers OR their
   * own reasons into it. Today those are "a bundled poster is rendered instead,
   * so a fetched cover would be discarded" (the welcome/demo library) and "this
   * element hasn't neared its scroll window yet" (see {@link useLibraryCover}).
   */
  disabled?: boolean;
}

/**
 * Resolves a library's cover thumbnail, walking `latestMediaIds` on error.
 *
 * The fallback chain matters: audio-only media has no visual thumbnail, so the
 * first candidate frequently 404s in audio-heavy libraries. Without it a library
 * shows a placeholder where a perfectly good thumbnail exists further down the
 * list.
 *
 * Shared by `LibraryCard` (Libraries grid), `LibraryListRow` (Libraries list),
 * and `LibrarySwitchRow` (search bar) so all three resolve covers identically.
 *
 * NOTE: `useMediaThumbnail` is a real hook (`useState`/`useEffect` inside
 * `useMediaFilePreview`), so it must be called unconditionally. `LibrarySwitchRow`
 * previously called it inside a ternary, which changed the hook count whenever a
 * library gained or lost `latestMediaIds` while mounted. Keep the call
 * unconditional and gate the *result*, never the call.
 */
export function useLibraryThumbnail(
  library: LibraryStore,
  { thumbnailMediaId, hoveredMediaId, disabled = false }: UseLibraryThumbnailOptions = {},
) {
  const [hasFailed, setHasFailed] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);

  const latestMediaIds = library.latestMediaIds;
  const maxAttempts = Math.min(latestMediaIds?.length ?? 0, MAX_FALLBACK_ATTEMPTS);
  const latestMediaId = latestMediaIds?.[fallbackIndex];

  /**
   * The candidates this hook would actually try, as a stable string.
   *
   * Deliberately *not* `latestMediaId`: that is `latestMediaIds[fallbackIndex]`, so
   * resetting `fallbackIndex` below would change it back and the effect would loop.
   * Keying on the candidate list itself has no such feedback.
   */
  const candidateKey = latestMediaIds?.slice(0, MAX_FALLBACK_ATTEMPTS).join(',') ?? '';

  const candidateId = hoveredMediaId || thumbnailMediaId || latestMediaId;

  // The hook call stays unconditional (that was the whole point of extracting it),
  // but the *arguments* are gated: `usePreviewToken` no-ops on an undefined
  // libraryId, so passing nothing when there is nothing to resolve skips a preview
  // token request per media-less library. Without this, opening the search bar's
  // library switcher fires one token request per empty library and throws away every
  // result.
  const shouldResolve = !disabled && !!candidateId;
  const resolved = useMediaThumbnail(
    shouldResolve ? library.libraryId : undefined,
    shouldResolve ? candidateId : undefined,
  );

  // A new target resets the chain — a previous failure says nothing about different
  // media. `candidateKey` matters as much as the explicit ids: without it `hasFailed`
  // latches for the component's lifetime, so a library whose only candidate had no
  // thumbnail would keep showing the placeholder even after the user adds media that
  // does have one.
  useEffect(() => {
    setHasFailed(false);
    setFallbackIndex(0);
  }, [thumbnailMediaId, hoveredMediaId, candidateKey]);

  /**
   * Advance to the next `latestMediaIds` candidate, or give up. Only the
   * implicit "latest media" path walks the chain: an explicit hover or persisted
   * choice that fails should fall straight through to the placeholder rather
   * than silently showing a different library's media.
   *
   * Memoised because `AuthenticatedImage` lists `onError` in its fetch effect's
   * dependencies: a fresh closure each render tears that effect down and re-runs
   * it, which aborts the in-flight request and releases/re-acquires the cache
   * entry (`ImageCacheService` has no in-flight dedup). Without this, a covers-
   * loading list view re-fires every visible thumbnail request on every render.
   */
  const onError = useCallback(() => {
    const usingLatestFallback = !hoveredMediaId && !thumbnailMediaId;
    if (usingLatestFallback && fallbackIndex + 1 < maxAttempts) {
      setFallbackIndex((i) => i + 1);
    } else {
      setHasFailed(true);
    }
  }, [hoveredMediaId, thumbnailMediaId, fallbackIndex, maxAttempts]);

  return {
    url: hasFailed || disabled ? undefined : (resolved?.url ?? undefined),
    token: disabled ? undefined : (resolved?.token ?? undefined),
    onError,
  };
}

interface UseLibraryCoverOptions extends UseLibraryThumbnailOptions {
  /**
   * How the surface lays its covers out, which picks the overscan — see
   * {@link LIBRARY_COVER_OVERSCAN}. Defaults to `list`; only the card grid is `grid`.
   */
  layout?: keyof typeof LIBRARY_COVER_OVERSCAN;
  /**
   * While false the cover cannot start loading. Suppresses new loads only — one
   * already resolved stays resolved. For a container that hides its content
   * without clipping it; see `LibrarySwitchRow`.
   */
  enabled?: boolean;
}

/**
 * {@link useLibraryThumbnail}, deferred until the element nears its scroll window.
 *
 * Wraps the moving parts so the list surfaces can't drift apart on the overscan or
 * on OR-ing "not in view" into `disabled`. Used by the Libraries grid and list and
 * by the picker rows; `LibrariesTable` (DataTable windows its own rows) and
 * `LibraryIcon` (a handful at a time) call the plain hook and stay eager.
 *
 * The scroll container is resolved from the DOM rather than passed in. Each of these
 * three surfaces has a different one — the picker's popover scrolls itself, while the
 * Libraries grid and list scroll at a page-level wrapper several levels up — and the
 * ref that looked correct for all three was right for only one, which made the whole
 * feature a no-op on the two that mattered most.
 *
 * The element ref stays caller-owned — this hook has no DOM of its own, and the
 * three callers observe different element types.
 *
 * **Load once, never unload.** `useInView`'s latch is one-shot, so a cover survives
 * scrolling back out of view. Dropping it would release the `ImageCacheService`
 * refcount and re-fetch on re-entry, and that cache has no in-flight dedup, so a
 * fast scroll would fire duplicate requests for the same cover. Sticky also keeps
 * acquire/release balanced — see `.claude/rules/ref-counting-caches.md`.
 */
export function useLibraryCover<T extends Element>(
  library: LibraryStore,
  elementRef: RefObject<T | null>,
  { layout = 'list', enabled, disabled, ...thumbnailOptions }: UseLibraryCoverOptions = {},
) {
  const inView = useInView(elementRef, {
    root: 'nearest-scrollable',
    rootMargin: LIBRARY_COVER_OVERSCAN[layout],
    // A caller-supplied `disabled` is permanent in practice (the welcome library and the demo
    // row render bundled posters), and observing something whose answer can't matter costs an
    // `IntersectionObserver` plus a `getComputedStyle` ancestor walk per card. Folding it in
    // here rather than only into the result skips both.
    enabled: enabled !== false && !disabled,
  });

  return useLibraryThumbnail(library, { ...thumbnailOptions, disabled: disabled || !inView });
}
